import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { FieldLabel } from "../shared/FieldHint";
import { formatET } from "../../lib/dateUtils";
import { api, formatApiErrorDetail } from "../../lib/api";

export function ManagerDocumentsTab() {
  const [agreement, setAgreement] = useState(null);
  const [form, setForm] = useState({ full_legal_name: "", email: "", phone: "", agree: false });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justSigned, setJustSigned] = useState(false);
  const [signedAgreements, setSignedAgreements] = useState([]);

  useEffect(() => {
    api.get("/manager/agreement")
      .then(({ data }) => setAgreement(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    api.get("/manager/signed-agreements").then(({ data }) => setSignedAgreements(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree) { toast.error("Check the agree box to sign"); return; }
    if (!form.full_legal_name.trim()) { toast.error("Full legal name is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/manager/agreement/sign", form);
      toast.success("Agreement submitted successfully");
      setJustSigned(true);
      const { data } = await api.get("/manager/agreement");
      setAgreement(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !agreement) return null;

  return (
    <div data-testid="manager-documents-tab" className="max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-serif text-xl text-igc-cream tracking-tight">IGC Manager Partner Agreement ({agreement.agreement_code})</h2>
        {agreement.signed ? (
          <span className="text-[10px] px-3 py-1.5 uppercase tracking-wide bg-igc-success/10 text-igc-success border border-igc-success/30" data-testid="manager-agreement-signed-badge">
            Signed — {formatET(agreement.signed_at, "MMM d, yyyy")}
          </span>
        ) : (
          <span className="text-[10px] px-3 py-1.5 uppercase tracking-wide bg-igc-gold/15 text-igc-goldText border border-igc-gold/40" data-testid="manager-agreement-action-required-badge">
            Action Required — Sign Now
          </span>
        )}
      </div>

      {justSigned && agreement.signed && (
        <div className="bg-igc-success/10 border border-igc-success/30 p-4 mb-6 text-igc-success text-sm" data-testid="manager-agreement-confirmation-banner">
          Your agreement was submitted successfully. A signed copy has been emailed to you and to IGC — no further action needed.
        </div>
      )}

      <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-6 whitespace-pre-wrap text-igc-cream text-sm leading-relaxed" data-testid="manager-agreement-text">
        {agreement.agreement_text}
      </div>

      {agreement.signed ? (
        <p className="text-igc-muted text-sm" data-testid="manager-agreement-already-signed-note">Signed by {agreement.full_legal_name} — thanks, no further action needed.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3 bg-igc-surface border border-igc-gold/15 p-5" data-testid="manager-agreement-sign-form">
          <Input data-testid="manager-agreement-legal-name" placeholder="Full legal name" title="Type your full legal name — this becomes your digital signature." value={form.full_legal_name} onChange={(e) => setForm({ ...form, full_legal_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="manager-agreement-email" placeholder="Email" title="Your contact email on this signed agreement." value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
            <Input data-testid="manager-agreement-phone" placeholder="Phone" title="Your contact phone number on this signed agreement." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
          </div>
          <Input data-testid="manager-agreement-date" disabled value={new Date().toLocaleDateString()} title="Today's date, recorded automatically as the signing date." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-muted" />
          <label className="flex items-center gap-2 text-igc-cream text-sm">
            <Checkbox data-testid="manager-agreement-agree-checkbox" checked={form.agree} onCheckedChange={(v) => setForm({ ...form, agree: !!v })} />
            I have read and agree to the terms of this Agreement
            <FieldLabel hint="Checking this and submitting counts as your digital signature on the agreement above." testId="manager-agreement-agree-hint" />
          </label>
          <Button data-testid="manager-agreement-sign-button" type="submit" disabled={submitting || !form.agree} title="Submits your signature and finalizes this agreement." className="bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40 disabled:cursor-not-allowed">I Accept & Sign</Button>
        </form>
      )}

      <div className="mt-10">
        <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-1" title="Every client agreement one of your reps' clients has signed, auto-collected from agreements.igchq.com.">Your Team's Signed Client Agreements</h2>
        <p className="text-igc-muted text-sm mb-4" title="Every onboarding agreement a client of your reps digitally signs on agreements.igchq.com lands here automatically.">Populated automatically the instant a client of one of your reps signs their onboarding agreement.</p>
        {signedAgreements.length === 0 && (
          <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="manager-signed-agreements-empty">No signed agreements yet for your team.</p>
        )}
        <div className="space-y-2">
          {signedAgreements.map((a) => (
            <div key={a.id} className="bg-igc-surface border border-igc-gold/15 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`manager-signed-agreement-${a.id}`}>
              <div>
                <p className="text-igc-cream text-sm">{a.full_legal_name || "Unknown signer"}</p>
                <p className="text-igc-muted text-xs">{a.email || "no email"} — {a.agreement_name || "Onboarding Agreement"} ({a.agreement_ref || "no ref"})</p>
                {a.rep_display_name && <p className="text-igc-muted text-xs">Rep: {a.rep_display_name} ({a.rep_code})</p>}
              </div>
              <p className="text-igc-muted text-xs">Signed {formatET(a.received_at, "MMM d, yyyy")}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
